/**
 * Operations sign-in gate. Prompts for a HepCURE PIN, exchanges it for
 * an `operations_edit`-scoped token via /api/pp/auth/user-pin.
 */
import { useEffect, useRef, useState } from "react";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { loginWithPin } from "@/lib/operations/api";

export default function AuthGate({ onAuthed }) {
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await loginWithPin(pin.trim());
      if (res.ok) {
        onAuthed?.(res.user);
      } else {
        const map = {
          not_found: "PIN not recognised.",
          inactive: "This account is inactive.",
          no_permission: "You don't have permission to manage expenses.",
          pin_format: "PIN must be 4–8 characters.",
        };
        setError(map[res.reason] || "Invalid PIN.");
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign-in failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="ops-authgate">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-lg border border-slate-200 p-8">
        <div className="h-12 w-12 rounded-full bg-brand-mist flex items-center justify-center text-brand-navy mb-4">
          <KeyRound className="h-5 w-5" />
        </div>
        <h1 className="text-2xl font-bold font-heading text-brand-navy">Operations sign-in</h1>
        <p className="mt-1 text-sm text-slate-500">
          Enter your PIN to manage expenses.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">PIN</span>
            <div className="mt-1.5 relative">
              <input
                ref={ref}
                type={showPin ? "text" : "password"}
                inputMode="numeric"
                autoComplete="off"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="w-full h-12 rounded-lg border border-slate-200 bg-white px-4 pr-11 text-lg tracking-widest text-brand-navy focus:outline-none focus:ring-2 focus:ring-brand-teal/40 focus:border-brand-teal"
                data-testid="ops-pin-input"
              />
              <button
                type="button"
                onClick={() => setShowPin((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md text-slate-400 hover:text-brand-navy hover:bg-slate-100 flex items-center justify-center"
                aria-label={showPin ? "Hide PIN" : "Show PIN"}
                tabIndex={-1}
              >
                {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>
          {error && <p className="text-sm text-red-600" data-testid="ops-pin-error">{error}</p>}
          <button
            type="submit"
            disabled={busy || !pin.trim()}
            className="w-full h-12 rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition"
            data-testid="ops-pin-submit"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {busy ? "Signing in…" : "Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
